import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, TriangleAlert, History, Users, MapPin, Camera, GitBranch, Hand } from "lucide-react";
import { Section, InfoItem } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/Photo";
import { StopButton, ReviseButton } from "@/components/take5/Dialogs";
import { CompleteButton } from "@/components/take5/CompleteButton";
import { Take5Header } from "@/components/take5/Take5Header";
import { PdfButton } from "@/components/PdfButton";
import { useOfflineState } from "@/lib/offline/state";
import { useAuth } from "@/context/AuthContext";
import { api, errMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { DECISION_LABEL } from "@/lib/take5";
import { isConform, safeAnswer } from "@/lib/questionDraw";
import { participantName } from "@/components/take5/Participants";

const TYPE_TAG = { team_member: "équipe", workplace_guest: "workplace", external_guest: "invité" };

const ParticipantList = ({ doc }) => (
  <div className="mt-3 text-sm" data-testid="take5-view-participants">
    <p><Users className="mr-1 inline h-4 w-4" /> Équipe : {doc.team_name || "Sans équipe"} · Responsable : <strong data-testid="take5-view-responsible">{doc.responsible_name || doc.user_name}</strong></p>
    <ul className="mt-2 flex flex-wrap gap-2">
      {doc.participants.map((p, i) => (
        <li key={i} data-testid={`take5-view-participant-${i}`} className="rounded-full border border-border px-3 py-1">
          {participantName(p)}{TYPE_TAG[p.participant_type] && <span className="ml-1 text-xs text-muted-foreground">({TYPE_TAG[p.participant_type]})</span>}
        </li>
      ))}
    </ul>
  </div>
);

const Decisions = ({ doc }) => (
  <Section title="Décisions" icon={History} testId="take5-decisions-section">
    {!doc.decisions.length && <p className="text-sm text-muted-foreground">Aucune décision n'a été prise avant l'archivage.</p>}
    <ol className="space-y-2">
      {doc.decisions.map((x, i) => (
        <li key={i} data-testid={`decision-${i}`} className="rounded-lg border border-border bg-background/40 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={x.decision} />
            <span className="font-data text-xs text-slate-300">{fmtDateTime(x.at, doc.site)} · {x.by_name}{x.by_role === "superviseur" ? " (superviseur)" : ""}</span>
          </div>
          {x.reason && <p className="mt-2 whitespace-pre-wrap text-sm"><Hand className="mr-1 inline h-3.5 w-3.5 text-red-300" />Raison : {x.reason}</p>}
        </li>
      ))}
    </ol>
    {doc.status === "archivee" && doc.decision && <p data-testid="take5-original-decision" className="mt-3 text-sm text-muted-foreground">Décision d'origine conservée : <strong className="text-foreground">{DECISION_LABEL[doc.decision]}</strong></p>}
  </Section>
);

const Revisions = ({ doc }) => doc.revisions.length > 1 && (
  <Section title="Révisions" icon={GitBranch} testId="take5-revisions-section">
    {doc.revision_reason && <p className="mb-3 text-sm">Raison de cette révision : <span className="whitespace-pre-wrap">{doc.revision_reason}</span></p>}
    <ul className="space-y-2">
      {doc.revisions.map((r) => (
        <li key={r.id}>
          <Link to={`/take5/${r.id}`} data-testid={`revision-link-${r.revision}`} className={`flex flex-wrap items-center gap-2 rounded-lg border p-3 ${r.id === doc.id ? "border-amber-500/60" : "border-border hover:border-slate-500"}`}>
            <span className="font-data text-sm">Révision {r.revision}</span>
            <StatusBadge status={r.status} />
            <span className="font-data text-xs text-muted-foreground">{fmtDateTime(r.start_at, doc.site)}</span>
          </Link>
        </li>
      ))}
    </ul>
  </Section>
);

const ACTION_BTN = "h-11 flex-1 px-4 sm:flex-none";

export const Take5View = ({ doc, onChange }) => {
  const { online } = useOfflineState();
  const { user } = useAuth();
  const navigate = useNavigate();
  const owner = doc.user_id === user.id && !doc.archived;
  const stop = async (reason) => {
    try {
      const r = await api.post(`/take5s/${doc.id}/stop`, { reason });
      toast.success("Travail arrêté : la décision est enregistrée.");
      onChange(r.data);
      return null;
    } catch (e) { return errMessage(e); }
  };
  const revise = async (reason) => {
    try {
      const r = await api.post(`/take5s/${doc.id}/revise`, { reason });
      toast.success("Nouvelle révision créée.");
      navigate(`/take5/${r.data.id}`);
      return null;
    } catch (e) { return errMessage(e); }
  };

  const canRevise = owner && online && !doc.superseded_by && (doc.status === "autorisee" || doc.status === "arretee");
  const canComplete = (doc.can_complete ?? (owner && doc.status === "autorisee" && !doc.superseded_by && !doc.archived)) && !doc.sync_conflict;
  const canStop = owner && doc.status === "autorisee";
  return (
    <div className="space-y-5">
      <Take5Header doc={doc} actions={<>{online && !doc.sync_conflict && <PdfButton url={`/take5s/${doc.id}/pdf`} testId="take5-pdf-button" />}</>} />
      {doc.sync_conflict && <p data-testid="take5-sync-conflict" role="alert" className="rounded-md border border-red-500/60 bg-red-950/40 p-3 text-sm text-red-100">Synchronisation en conflit : {doc.sync_conflict}</p>}
      {doc.archived && <p data-testid="take5-archived-notice" className="rounded-lg border border-slate-500/60 bg-slate-800/60 p-3 text-sm">Ce Take 5 est archivé : il est consultable mais ne peut plus être modifié.</p>}
      {doc.superseded_by && <Link to={`/take5/${doc.superseded_by}`} data-testid="take5-superseded-link" className="block rounded-lg border border-orange-500/60 bg-orange-950/40 p-3 text-sm text-orange-100">Ce Take 5 a été réévalué. Voir la nouvelle révision →</Link>}
      {doc.user_id !== user.id && doc.mode === "collectif" && (doc.read_only_participant || doc.participants.some((p) => p.user_id === user.id)) && (
        <p data-testid="take5-participant-notice" className="rounded-lg border border-amber-500/60 bg-amber-500/10 p-3 text-sm">Participant · Take 5 collectif de {doc.responsible_name || doc.user_name} — consultation en lecture seule.</p>
      )}
      {(canComplete || canRevise || canStop) && (
        <div data-testid="take5-actions-row" className="flex flex-wrap gap-2">
          {canComplete && <CompleteButton id={doc.id} onDone={onChange} className={ACTION_BTN} />}
          {canRevise && <ReviseButton onRevise={revise} className={ACTION_BTN} />}
          {canStop && <StopButton onStop={stop} className={`${ACTION_BTN} border`} />}
        </div>
      )}
      <Section title="Contexte" icon={MapPin} testId="take5-view-context">
        <div className="grid gap-4 sm:grid-cols-3">
          <InfoItem label="Site" value={doc.site.name} />
          <InfoItem label="Lieu précis" value={doc.location || "—"} testId="take5-view-location" />
          <InfoItem label="Mode" value={doc.mode === "collectif" ? "Collectif" : "Individuel"} />
        </div>
        {doc.mode === "collectif" && <ParticipantList doc={doc} />}
      </Section>
      <Section title="Préparation" icon={ClipboardList} testId="take5-view-questions">
        <ul className="space-y-2">
          {doc.questions.map((q, i) => (
            <li key={q.id} data-testid={`take5-view-question-${i + 1}`} className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm">
              <span>{q.text}</span>
              <span className="shrink-0 text-right">
                <span className={`font-data font-bold ${isConform(q, doc.answers[q.id]) ? "text-emerald-300" : "text-red-300"}`}>{doc.answers[q.id] ? doc.answers[q.id].toUpperCase() : "—"}</span>
                <span className="block text-[11px] text-muted-foreground">attendue : {safeAnswer(q).toUpperCase()}</span>
              </span>
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Dangers et mesures" icon={TriangleAlert} testId="take5-view-hazards">
        {doc.no_hazard_confirmed && !doc.hazards.length && <p className="text-sm text-emerald-300">Confirmé : aucun danger identifié.</p>}
        {!doc.no_hazard_confirmed && !doc.hazards.length && <p className="text-sm text-muted-foreground">Aucun danger renseigné.</p>}
        <div className="space-y-3">
          {doc.hazards.map((h, i) => (
            <div key={h.key} data-testid={`view-hazard-${i}`} className="rounded-lg border border-border p-3">
              <p className="font-data text-[11px] uppercase tracking-widest text-amber-400">{h.family_name}</p>
              <p className="font-bold">{h.label}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {h.measures.map((m, j) => <li key={j} data-testid={`view-hazard-${i}-measure-${j}`} className="whitespace-pre-wrap">{m}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>
      {doc.photo_ids.length > 0 && (
        <Section title="Photos" icon={Camera}>
          <div className="flex flex-wrap gap-3">{doc.photo_ids.map((p) => <AuthImage key={p} fileId={p} alt="Photo" className="h-28 w-28 rounded-lg" />)}</div>
        </Section>
      )}
      <Decisions doc={doc} />
      <Revisions doc={doc} />
    </div>
  );
};
